import {meService} from "@/src/modules/core/services/me";
import {useMeStore} from "@/src/modules/core/store/me";
import type {ParamsOptions} from "@/src/modules/core/types";

import {companyOnGuardApi} from "../api";
import type {CompanyOnGuardAssigneeForUpdate} from "../types";

/** User list `filter[role]` values — API role names (spaces), not role keys. */
const ELIGIBLE_ROLE_NAMES = ["admin", "supervisor", "senior supervisor"] as const;

class CompanyOnGuardService {
  private isResidentRole(): boolean {
    return Boolean(useMeStore.getState().me?.isResident);
  }

  async getAssignees(companyId: number, options?: ParamsOptions) {
    const {data} = await companyOnGuardApi.getAssignees(companyId, options);
    const rows = Array.isArray(data.data) ? data.data : [];
    return {
      data: rows,
      meta: data.meta ?? {total: rows.length, from: 0, to: rows.length},
    };
  }

  async createAssignee(companyId: number, payload: Parameters<typeof companyOnGuardApi.createAssignee>[1]) {
    const {data} = await companyOnGuardApi.createAssignee(companyId, payload);
    return data.data;
  }

  async updateAssignee(
    companyId: number,
    assigneeId: number,
    payload: CompanyOnGuardAssigneeForUpdate,
  ) {
    const {data} = await companyOnGuardApi.updateAssignee(companyId, assigneeId, payload);
    return data.data;
  }

  async deleteAssignee(companyId: number, assigneeId: number) {
    await companyOnGuardApi.deleteAssignee(companyId, assigneeId);
  }

  /** Admin nav + page access — same capability as roster list. */
  getCanShowSection(): boolean {
    return this.getCanListRoster();
  }

  getCanListRoster(): boolean {
    if (this.isResidentRole()) return false;
    return meService.getHasPermission("company onguard");
  }

  /** Dispatch desk panel — omit entirely for residents. */
  getCanViewSupervisorOnCallPanel(): boolean {
    return !this.isResidentRole();
  }

  getCanCreate(): boolean {
    return meService.getHasPermission("company update");
  }

  getCanUpdate(): boolean {
    return meService.getHasPermission("company update");
  }

  getCanDelete(): boolean {
    return meService.getHasPermission("company delete");
  }

  getEligibleRoleFilter(): string {
    return ELIGIBLE_ROLE_NAMES.join(",");
  }
}

export const companyOnGuardService = new CompanyOnGuardService();
